import { useEffect, useState } from "react";
import { Fetch } from "../../shared/Fetch";
import { Toolbar } from "primereact/toolbar";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { ICompanyMastDropdown } from "../../models/IDropdown";

export interface IGroup {
  id?: number;
  groupName: string;
  count?: number;
}

export interface ICompany {
  id: number;
  companyName: string;
}

export interface IGroupCompany {
  id: number;
  companyId: number;
  companyName: string;
  groupId: number;
}

interface IProps {
  setModal: (modal: { open: boolean; singleData: IGroup }) => void;
  reload: boolean;
  toast: any;
}

const EmptyGroup: IGroup = {
  groupName: "",
};

export const Table = ({ reload, setModal, toast }: IProps) => {
  const [data, setData] = useState<IGroup[]>([]);
  const [countModal, setCountModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IGroup | null>(null);
  const [companies, setCompanies] = useState<Array<ICompanyMastDropdown>>();
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [groupCompanies, setGroupCompanies] = useState<IGroupCompany[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  const loadTableData = async () => {
    const response = await Fetch("Company_Grouping/getGroup", "GET", null, null, null);
    if (response) {
      const result = await response.json();
      setData(result || []);
    }
  };

  const loadCompanies = async () => {
    const response = await Fetch("Dropdown/company", "GET", null, null, null);
    if (response) {
      const data: Array<ICompanyMastDropdown> = await response.json();
      const emptyData = {
        company_ID: 0,
        company_Name: "-- SELECT --",
      };
      data.unshift(emptyData);
      setCompanies(data);
    }
  };

  const loadGroupCompanies = async (groupId: number) => {
    const response = await Fetch(`Company_Grouping/getCompanyGroup/${groupId}`, "GET", null, "", null);
    if (response) {
      const result: IGroupCompany[] = await response.json();
      setGroupCompanies(result || []);
    }
  };

  useEffect(() => {
    loadTableData();
  }, [reload]);

  const openCountModal = async (rowData: IGroup) => {
    setSelectedGroup(rowData);
    setSelectedCompanies([]);
    await loadCompanies();
    await loadGroupCompanies(rowData.id!);
    setCountModal(true);
  };

  const handleSubmitCompanies = async () => {
    setSubmitLoading(true);
    try {
      const payload = selectedCompanies.map((companyId) => ({
        Group_ID: selectedGroup?.id,
        Company_ID: companyId,
      }));
      const response = await Fetch("Company_Grouping/saveGroupMapping", "POST", payload, null, null);
      if (response && response.ok) {
        const data = await response.json();
        // console.log(data);
        // const addedCount = data.addedCompany?.length || 0;
        // const skippedCount = data.skippedCompany?.length || 0;
        // console.log(data);

        const message = (
          <div>
            <ul style={{ marginTop: "6px", paddingLeft: "18px" }}>
              {data.addedCompany?.map((item: string, i: number) => (
                <li key={`a-${i}`} style={{ fontSize: "13px", color: "green" }}>
                  {item} (Added)
                </li>
              ))}

              {data.skippedCompany?.map((item: string, i: number) => (
                <li key={`s-${i}`} style={{ fontSize: "13px", color: "#dc2626" }}>
                  {item} (Already exists)
                </li>
              ))}
            </ul>
            {!data.addedCompany?.length && !data.skippedCompany?.length && <div>No changes made.</div>}
          </div>
        );

        const severity = data.skippedCompany?.length > 0 && data.addedCompany?.length === 0 ? "error" : data.addedCompany?.length > 0 ? "success" : "info";

        toast?.current?.show({
          severity,
          summary: "Result",
          detail: message,
        });
        setSelectedCompanies([]);
        await loadGroupCompanies(selectedGroup?.id!);
        await loadTableData();
      } else {
        let errorMessage = "Something went wrong.";

        try {
          const err = await response?.json();
          errorMessage = err?.message || errorMessage;
        } catch {}

        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
        });
      }
    } catch {
      toast?.current?.show({ severity: "error", summary: "Error", detail: "Something went wrong." });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDeleteGroupCompany = (item: IGroupCompany) => {
    confirmDialog({
      message: `Remove "${item.companyName}" from this group?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        const response = await Fetch(`Company_Grouping/deleteGroupCompany/${item.id}`, "DELETE", null, "", null);

        if (response && response.ok) {
          setGroupCompanies((prev) => prev.filter((x) => x.id !== item.id));

          toast?.current?.show({ severity: "success", summary: "Deleted", detail: "Company removed from group." });
          await loadGroupCompanies(item.groupId);
          await loadTableData();
        } else {
          toast?.current?.show({ severity: "error", summary: "Error", detail: "Failed to delete." });
        }
      },
    });
  };

  const openNew = () => {
    setModal({ open: true, singleData: EmptyGroup });
  };

  const leftToolbarTemplate = () => <Button label="New" icon="pi pi-plus" className="p-button-success" onClick={openNew} />;

  return (
    <>
      <ConfirmDialog />

      <Toolbar className="mb-2" left={leftToolbarTemplate} />

      <DataTable value={data} paginator rows={10} size="small">
        <Column header="S.No" body={(_rowData, options) => options.rowIndex + 1} />
        <Column field="groupName" header="Group Name" />
        <Column
          field="count"
          header="Count"
          body={(rowData: IGroup) => (
            <span className="text-primary font-semibold" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => openCountModal(rowData)}>
              {rowData.count ?? 0}
            </span>
          )}
        />
        <Column header="Action" body={(rowData: IGroup) => <i className="pi pi-pencil text-primary" style={{ cursor: "pointer" }} onClick={() => setModal({ open: true, singleData: rowData })} />} />
      </DataTable>

      <Dialog header={`Companies in "${selectedGroup?.groupName}"`} visible={countModal} style={{ width: "80vw" }} modal contentStyle={{ height: "75vh", display: "flex", flexDirection: "column" }} maximizable onHide={() => setCountModal(false)}>
        <div className="flex align-items-center gap-3 mb-3">
          <MultiSelect value={selectedCompanies} options={companies} onChange={(e) => setSelectedCompanies(e.value)} optionLabel="company_Name" optionValue="company_ID" placeholder="Select Companies" display="chip" filter className="w-full" style={{ flex: 1 }} />

          <Button label="Submit" icon="pi pi-plus" className="p-button-success" loading={submitLoading} onClick={handleSubmitCompanies} disabled={!selectedCompanies.length} />
        </div>

        <div style={{ borderTop: "1px solid #ddd", marginBottom: "10px" }} />

        <div style={{ flex: 1, overflow: "auto" }}>
          <DataTable value={groupCompanies} size="small" stripedRows showGridlines emptyMessage="No companies added yet." style={{ minHeight: "100%" }}>
            <Column header="S.No" body={(_rowData, options) => options.rowIndex + 1} style={{ width: "80px", textAlign: "center" }} />
            <Column field="companyName" header="Company Name" />
            <Column header="Action" style={{ width: "100px", textAlign: "center" }} body={(rowData: IGroupCompany) => <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => handleDeleteGroupCompany(rowData)} />} />
          </DataTable>
        </div>
      </Dialog>
    </>
  );
};
